// @flow

import React, { type Node, Suspense } from 'react';
import { getPaymentType, getPaymentTypeCode, useIncomingPayment, getCountry, getThreeDSecureRedirectUrl } from '@tectonics/prs/incoming-payment';
import { LoadingContent } from './loading-content';

import { type Country } from '@tectonics/prs/country';

const PendingPage = React.lazy(() => import('./pending-page'));
const BoletoPage = React.lazy(() => import('./boleto-page'));
const EfectyPage = React.lazy(() => import('./efecty-page'));
const SpeiPage = React.lazy(() => import('./spei-page'));
const NequiPage = React.lazy(() => import('./nequi-page'));
const CreditCardPage = React.lazy(() => import('./creditcard-page'));
const RedirectPageContent = React.lazy(() => import('./redirect-page'));
const PixPage = React.lazy(() => import('./pix-page'));
const DebinPage = React.lazy(() => import('./debin-page'));
const OtpPage = React.lazy(() => import('./otp-page'));
const BankTransferPage = React.lazy(() => import('./bank-transfer-page'));

export function PageContent(): Node {
  const incomingPayment = useIncomingPayment();
  const paymentType = getPaymentType(incomingPayment) || 'null';
  const paymentTypeCode = getPaymentTypeCode(incomingPayment) || 'null';
  const country = getCountry(incomingPayment);
  const threeDSecureRedirectUrl = getThreeDSecureRedirectUrl(incomingPayment) || null;
  const hasOtp = (incomingPayment.hasOwnProperty('status_otp')) || false;
  return (
    <Suspense fallback={<LoadingContent />}>
      <PageContentImpl paymentType={paymentType} paymentTypeCode={paymentTypeCode} country={country} threeDSecureRedirectUrl={threeDSecureRedirectUrl} hasOtp={hasOtp} />
    </Suspense>
  );
}

type PageContentImplProps = {|
  paymentType: string,
  paymentTypeCode: string,
  country: Country,
  threeDSecureRedirectUrl: ?string,
  hasOtp: boolean,
|};

function PageContentImpl({ paymentType, paymentTypeCode, country, threeDSecureRedirectUrl, hasOtp }: PageContentImplProps): Node {
  switch(paymentType) {
    case 'boleto':
      return <BoletoPage />;

    case 'cash':
      return <CashPageContentImpl country={country} />;

    case 'oxxo':
    case 'pagoefectivo':
      return <PendingPage />;

    case 'spei':
      return <SpeiPage />;

    case 'creditcard':
    case 'debitcard':
      if (threeDSecureRedirectUrl) {
        return <RedirectPageContent />;
      }
      return <CreditCardPage />;

    case 'ewallet':
      return <EwalletPageComtentImpl country={country} hasOtp={hasOtp} paymentTypeCode={paymentTypeCode} />;

    case 'banktransfer':
      return <BankTransferPageContentImpl paymentTypeCode={paymentTypeCode} country={country} />;

    case 'tef':
    case 'eft':
    case 'sencillito':
    case 'webpay':
    case 'multicaja':
    case 'servipag':
    case 'safetypay':
    case 'safetypay-cash':
    case 'safetypay-online':
    case 'externalpayprovider':
      if (paymentTypeCode === 'fawry-external') {
        return <PendingPage />;
      }
      return <RedirectPageContent />;

    case 'pix':
      return <PixPage />;

    default:
      throw new Error(`Pending Page: payment type: ${paymentType} is not implemented`);
  }
}

function BankTransferPageContentImpl(props: { paymentTypeCode: string, country: string }): Node {
  if (props.paymentTypeCode === 'debin') {
    return <DebinPage />;
  }

  switch (props.country) {
    case 'pe':
    case 'cl':
      return <BankTransferPage />;

    default:
      return <RedirectPageContent />;
  }
}

function EwalletPageComtentImpl(props: { country: Country, hasOtp: boolean, paymentTypeCode: string }): Node {
  switch (props.country) {
    case 'co':
      return <NequiPage />;

    case 'ke':
    case 'ng':
    case 'in':
    case 'tz':
    case 'ug':
    case 'zm':
    case 'eg':
    case 'gh':
      return (props.hasOtp) ? <OtpPage /> : <PendingPage />;

    default:
      return <RedirectPageContent />;
  }
}

function CashPageContentImpl(props: { country: Country }): Node {
  switch (props.country) {
    case 'ar':
    case 'bo':
    case 'mx':
    case 'gt':
    case 'cr':
    case 'pa':
    case 'sv':
      return <PendingPage />;

    case 'co':
      return <EfectyPage />;

    default:
      throw new Error(`CashPendingPage country ${props.country} is not implemented`);
  }
}
